"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function VoicesOfImpact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.15
  });
  const testimonials = [{
    quote: "Damien doesn't just speak; he commands the room. His 'Resilience Mindset' keynote completely shifted our executive team's approach to crisis management.",
    name: "Sarah Jenkins",
    title: "VP of Leadership, Global Tech",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/leader_sarah_jenkins.png",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/leader_sarah_jenkins.png-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/leader_sarah_jenkins.png-wvc-sizes",
    company: "GLOBAL TECH"
  }, {
    quote: "The energy Damien brings is infectious. Our annual summit has never seen such high engagement levels. He is the gold standard for keynote speakers.",
    name: "Robert Chen",
    title: "Event Director, Innovation Forum",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/director_robert_chen.png",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/director_robert_chen.png-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/director_robert_chen.png-wvc-sizes",
    company: "INNOVATION FORUM"
  }, {
    quote: "Leading with Empathy isn't just a talk—it's a transformation. Damien provided our managers with actionable tools that produced immediate results.",
    name: "Elena Rodriguez",
    title: "Chief People Officer, Retail Group",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/cpo_elena_rodriguez.png",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/cpo_elena_rodriguez.png-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/cpo_elena_rodriguez.png-wvc-sizes",
    company: "RETAIL GROUP"
  }, {
    quote: "If you want a speaker who challenges the status quo and leaves your audience breathless, Damien Cole is the only choice. Pure power.",
    name: "Marcus Thorne",
    title: "CEO, Venture Dynamics",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/ceo_marcus_thorne.png",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/ceo_marcus_thorne.png-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/ceo_marcus_thorne.png-wvc-sizes",
    company: "VENTURE DYNAMICS"
  }];
  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % testimonials.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [testimonials.length]);
  const goPrev = () => setActiveIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
  const goNext = () => setActiveIndex(prev => (prev + 1) % testimonials.length);
  const featured = testimonials[activeIndex];
  const previewOrder = [testimonials[(activeIndex + 1) % testimonials.length], testimonials[(activeIndex + 2) % testimonials.length], testimonials[(activeIndex + 3) % testimonials.length]];
  return <section data-section-id="339" id="voices-of-impact" className="bg-background text-foreground relative overflow-hidden py-24 md:py-36 px-4 sm:px-6 lg:px-8">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-20 -right-20 w-[600px] h-[600px] opacity-[0.08]">
        <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
          <path d="M100 10 L180 50 L180 130 L100 190 L20 130 L20 50 Z" stroke="var(--primary)" strokeWidth="2" fill="none" />
          <path d="M100 30 L160 62 L160 122 L100 170 L40 122 L40 62 Z" stroke="var(--primary)" strokeWidth="1" fill="none" />
          <path d="M100 50 L140 74 L140 116 L100 150 L60 116 L60 74 Z" stroke="var(--primary)" strokeWidth="0.5" fill="none" />
        </svg>
      </div>

      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} />

      <div ref={ref} className={`relative max-w-7xl mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="font-mono text-xs sm:text-sm tracking-[0.2em] text-primary mb-6 uppercase">
            The Command Stage
          </div>
          <h2 className="font-default text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter leading-[0.9] mb-6 uppercase">
            Voices of <span className="text-primary">Impact</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            Hear from the leaders and organizations that have experienced the high-octane transformation of a Damien Cole keynote.
          </p>
        </div>

        {}
        <div className="relative max-w-5xl mx-auto mb-12 md:mb-16">
          <div className="relative bg-card rounded-md p-8 sm:p-12 md:p-16 border border-border overflow-hidden shadow-2xl">
            {}
            <span aria-hidden="true" className="absolute top-4 left-4 w-6 h-6 border-t-2 border-l-2 border-primary" />
            <span aria-hidden="true" className="absolute top-4 right-4 w-6 h-6 border-t-2 border-r-2 border-primary" />
            <span aria-hidden="true" className="absolute bottom-4 left-4 w-6 h-6 border-b-2 border-l-2 border-primary" />
            <span aria-hidden="true" className="absolute bottom-4 right-4 w-6 h-6 border-b-2 border-r-2 border-primary" />

            {}
            <Quote aria-hidden="true" className="absolute top-8 left-8 sm:top-12 sm:left-12 text-primary opacity-10 size-16 sm:size-20" strokeWidth={1.5} />

            {}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: "repeating-linear-gradient(0deg, var(--primary) 0px, var(--primary) 1px, transparent 1px, transparent 4px)"
          }} />

            <div className="relative">
              <div key={activeIndex} className="transition-opacity duration-[400ms] ease-out">
                <blockquote className="font-default text-xl sm:text-2xl md:text-[28px] font-bold italic leading-[1.45] text-foreground mb-10 md:mb-12 pt-8 sm:pt-12">
                  &ldquo;{featured.quote}&rdquo;
                </blockquote>

                <div className="flex items-center justify-between flex-wrap gap-6 pt-6 border-t border-border">
                  <div className="flex items-center gap-4">
                    <SafeHydrate><Avatar className="size-14 ring-2 ring-primary ring-offset-2 ring-offset-card rounded-md">
                      <AvatarImage src={featured.avatar} data-wvc-srcset={featured.avatarSrcset} data-wvc-sizes={featured.avatarSizes} alt={featured.name} loading="lazy" />
                      <AvatarFallback className="bg-secondary text-secondary-foreground font-mono text-sm rounded-md">
                        {featured.name.split(" ").map(n => n[0]).join("")}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div>
                      <div className="font-default text-base font-extrabold text-foreground uppercase tracking-tight">{featured.name}</div>
                      <div className="text-sm text-muted-foreground font-default">{featured.title}</div>
                    </div>
                  </div>

                  <div className="font-mono text-xs tracking-[0.2em] text-primary border border-primary/30 rounded-md px-3 py-2 bg-primary/5">
                    {featured.company}
                  </div>
                </div>

                {}
                <div className="mt-8 flex items-center justify-between font-mono text-[10px] sm:text-xs text-muted-foreground tracking-widest">
                  <span>REF:DC-{String(activeIndex + 1).padStart(3, "0")}</span>
                  <span className="hidden sm:inline">STAGE_MOMENTUM: HIGH</span>
                  <span className="text-primary">AUTHENTICATED</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="relative max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6 gap-4">
            <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase">
              Stage Reports — {String(activeIndex + 1).padStart(2, "0")}/{String(testimonials.length).padStart(2, "0")}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" aria-label="Previous testimonial" onClick={goPrev} className="text-primary hover:bg-primary hover:text-primary-foreground border border-primary/20 rounded-md transition-colors">
                <ChevronLeft className="size-4" />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Next testimonial" onClick={goNext} className="text-primary hover:bg-primary hover:text-primary-foreground border border-primary/20 rounded-md transition-colors">
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {previewOrder.map((t, i) => <Button key={`${t.name}-${i}`} data-index={i} onClick={() => setActiveIndex((activeIndex + i + 1) % testimonials.length)} variant="ghost" className={`group relative text-left h-auto p-6 justify-start flex-col items-start bg-card rounded-md border transition-all duration-300 hover:border-primary ${i === 0 ? "border-primary shadow-lg shadow-primary/5" : "border-border"}`}>
                {}
                {i === 0 && <>
                    <span aria-hidden="true" className="absolute top-2 left-2 w-3 h-3 border-t border-l border-primary" />
                    <span aria-hidden="true" className="absolute top-2 right-2 w-3 h-3 border-t border-r border-primary" />
                    <span aria-hidden="true" className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-primary" />
                    <span aria-hidden="true" className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-primary" />
                  </>}

                <Quote aria-hidden="true" className="text-primary opacity-30 size-5 mb-4" strokeWidth={2} />

                <p className="text-sm text-foreground/90 leading-relaxed line-clamp-3 mb-6 font-default overflow-hidden whitespace-pre-line">
                  &ldquo;{t.quote}&rdquo;
                </p>

                <div className="flex items-center gap-3 pt-4 border-t border-border w-full">
                  <SafeHydrate><Avatar className="size-9 rounded-md">
                    <AvatarImage src={t.avatar} data-wvc-srcset={t.avatarSrcset} data-wvc-sizes={t.avatarSizes} alt={t.name} loading="lazy" />
                    <AvatarFallback className="bg-secondary text-secondary-foreground font-mono text-xs rounded-md">
                      {t.name.split(" ").map(n => n[0]).join("")}
                    </AvatarFallback>
                  </Avatar></SafeHydrate>
                  <div className="min-w-0">
                    <div className="font-default text-sm font-bold text-foreground truncate uppercase tracking-tight">{t.name}</div>
                    <div className="text-xs text-muted-foreground truncate font-default">{t.title}</div>
                  </div>
                </div>
              </Button>)}
          </div>

          {}
          <div className="flex items-center justify-center gap-2 mt-10">
            {testimonials.map((_, i) => <Button key={i} data-index={i} onClick={() => setActiveIndex(i)} variant="ghost" aria-label={`Go to testimonial ${i + 1}`} className={`h-1.5 px-0 rounded-md transition-all duration-300 ${i === activeIndex ? "w-10 bg-primary" : "w-4 bg-border hover:bg-primary/50"}`} />)}
          </div>
        </div>
      </div>
    </section>;
}